import { useState, useEffect, useRef } from 'react'
import type { RegistrationStep } from '../services/account/registration'

interface CardContent {
  step: RegistrationStep
  headline: string
  body: string
}

const CARDS: CardContent[] = [
  {
    step: 'authenticating',
    headline: 'Generating keys',
    body: 'Creating your encryption keys locally in this browser. Your private key never leaves your device.',
  },
  {
    step: 'registering',
    headline: 'Registering username',
    body: 'Registering {username} on the Ethereum Name Service. This takes 1\u20133 minutes.',
  },
  {
    step: 'inbox',
    headline: 'Setting up inbox',
    body: 'Creating your decentralized inbox on Swarm so others can send you files directly.',
  },
  {
    step: 'encryption',
    headline: 'Configuring encryption',
    body: 'Setting up end-to-end encryption. Only you and your recipients can read your files.',
  },
  {
    step: 'backup',
    headline: 'Backing up account',
    body: 'Saving an encrypted backup to the network. You can restore your account from any device.',
  },
  {
    step: 'complete',
    headline: 'Account ready',
    body: 'Your account is set up. You can also connect the Fairdrop MCP server to use it with AI tools.',
  },
]

// Minimum time (ms) a card is shown before advancing
const MIN_CARD_DISPLAY_MS = 8000

// For the long server call, advance through cards on a timer
// since we can't get granular progress from the single endpoint
const SERVER_CALL_CARD_INTERVAL_MS = 30000

interface RegistrationProgressProps {
  currentStep: RegistrationStep
  username: string
}

export function RegistrationProgress({ currentStep, username }: RegistrationProgressProps) {
  const [visibleIndex, setVisibleIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const lastStepChangeTime = useRef(Date.now())
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Map step to the card index it should show (at minimum)
  const stepToMinIndex: Record<RegistrationStep, number> = {
    authenticating: 0,
    registering: 1,
    inbox: 2,
    encryption: 3,
    backup: 4,
    complete: 5,
  }

  // When the real step changes, advance to at least that card
  useEffect(() => {
    const targetIndex = stepToMinIndex[currentStep]
    if (targetIndex > visibleIndex) {
      const elapsed = Date.now() - lastStepChangeTime.current
      const remaining = Math.max(0, MIN_CARD_DISPLAY_MS - elapsed)

      setTimeout(() => {
        setIsTransitioning(true)
        setTimeout(() => {
          setVisibleIndex(targetIndex)
          lastStepChangeTime.current = Date.now()
          setIsTransitioning(false)
        }, 300) // fade-out duration
      }, remaining)
    }
  }, [currentStep])

  // During the long 'registering' step (server call ~200s),
  // auto-advance through cards 1-3 on a timer so user isn't stuck
  useEffect(() => {
    if (currentStep === 'registering') {
      timerRef.current = setInterval(() => {
        setVisibleIndex(prev => {
          // Only auto-advance up to card index 3 (encryption)
          // Cards 4+ are driven by real step changes
          if (prev < 3) {
            setIsTransitioning(true)
            setTimeout(() => {
              setIsTransitioning(false)
            }, 300)
            lastStepChangeTime.current = Date.now()
            return prev + 1
          }
          return prev
        })
      }, SERVER_CALL_CARD_INTERVAL_MS)

      return () => {
        if (timerRef.current) clearInterval(timerRef.current)
      }
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [currentStep])

  const card = CARDS[visibleIndex]
  const isComplete = currentStep === 'complete'
  const progress = ((visibleIndex + 1) / CARDS.length) * 100

  // Replace {username} placeholder
  const body = card.body.replace('{username}', username)

  return (
    <div className="reg-progress">
      <div className="reg-progress-bar">
        <div
          className="reg-progress-bar-fill"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className={`reg-progress-card ${isTransitioning ? 'fade-out' : 'fade-in'}`}>
        <div className="reg-progress-step-indicator">
          {CARDS.map((_, i) => (
            <span
              key={i}
              className={`reg-progress-dot ${i === visibleIndex ? 'active' : ''} ${i < visibleIndex ? 'done' : ''}`}
            />
          ))}
        </div>

        <h3 className="reg-progress-headline">{card.headline}</h3>
        <p className="reg-progress-body">{body}</p>

        {!isComplete && (
          <p className="reg-progress-status">
            Setting up your account...
          </p>
        )}
      </div>
    </div>
  )
}
